import React, { useCallback, useEffect, useState } from 'react';
import { Bell, CheckCheck, Copy, ExternalLink, Loader2 } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';

function formatRelative(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const diff = Math.floor((Date.now() - then) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

const TYPE_STYLE = {
  offer_received: { badge: 'bg-[#9d3dff]', label: 'NEW OFFER' },
  offer_accepted: { badge: 'bg-emerald-500', label: 'ACCEPTED' },
  offer_declined: { badge: 'bg-rose-500',    label: 'DECLINED' },
};

export default function NotificationsBell() {
  const { isAuthed } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!isAuthed) return;
    setLoading(true);
    try {
      const list = await api.listNotifications();
      setNotifications(list);
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, [isAuthed]);

  // Poll every 25s while the user is authed
  useEffect(() => {
    if (!isAuthed) {
      setNotifications([]);
      return;
    }
    fetchAll();
    const t = setInterval(fetchAll, 25000);
    return () => clearInterval(t);
  }, [isAuthed, fetchAll]);

  // Also refresh on popover open
  useEffect(() => {
    if (open) fetchAll();
  }, [open, fetchAll]);

  const unread = notifications.filter((n) => !n.read).length;

  const markRead = async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try { await api.markRead(id); } catch { /* ignore */ }
  };

  const markAll = async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    try { await api.markAllRead(); } catch { /* ignore */ }
  };

  const copy = (text) => {
    try {
      navigator.clipboard?.writeText(text);
      toast({ title: 'Copied!' });
    } catch { /* ignore */ }
  };

  if (!isAuthed) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell-btn"
          aria-label="Notifications"
          className="relative h-10 w-10 rounded-xl bg-white border-2 border-[#3a1064] shadow-[0_3px_0_#3a1064] flex items-center justify-center text-[#3a1064] hover:bg-yellow-50 transition-colors"
        >
          <Bell className={`h-5 w-5 ${unread > 0 ? 'animate-pulse' : ''}`} />
          {unread > 0 && (
            <span
              data-testid="notifications-unread-badge"
              className="absolute -top-1.5 -right-1.5 min-w-[20px] h-[20px] px-1 rounded-full bg-[#ff2bd6] border-2 border-[#3a1064] text-[10px] font-black text-white flex items-center justify-center"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="notifications-popover"
        className="w-[360px] sm:w-[400px] p-0 bg-[#fff7d6] border-[3px] border-[#3a1064] rounded-2xl shadow-[0_6px_0_#3a1064] overflow-hidden"
        align="end"
      >
        <div className="bg-[#ffd900] border-b-[3px] border-[#3a1064] px-4 py-3 flex items-center gap-2">
          <Bell className="h-4 w-4 text-[#ff2bd6]" />
          <span className="font-black text-[#3a1064] text-sm uppercase tracking-wider flex-1">
            Notifications
          </span>
          {unread > 0 && (
            <button
              data-testid="mark-all-read-btn"
              onClick={markAll}
              className="text-[10px] font-black text-[#3a1064] underline underline-offset-2 hover:text-[#ff2bd6] flex items-center gap-1"
            >
              <CheckCheck className="h-3 w-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[440px] overflow-y-auto">
          {loading && notifications.length === 0 ? (
            <div className="py-10 text-center text-[#3a1064]/70 font-bold">
              <Loader2 className="h-5 w-5 mx-auto animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-10 px-6 text-center text-[#3a1064]/70 font-bold text-sm">
              No notifications yet.<br />
              <span className="text-xs">When the devs respond to your offers, they show up here.</span>
            </div>
          ) : (
            notifications.map((n) => {
              const style = TYPE_STYLE[n.type] || { badge: 'bg-[#3a1064]', label: 'INFO' };
              const link = n.data?.payment_link;
              const meet = n.data?.meet_up_note;
              return (
                <div
                  key={n.id}
                  data-testid={`notif-${n.id}`}
                  className={`px-4 py-3 border-b border-[#3a1064]/10 ${
                    n.read ? 'bg-white/40' : 'bg-white'
                  } hover:bg-yellow-50 transition-colors`}
                >
                  <div className="flex items-start gap-2">
                    <span className={`${style.badge} text-white text-[8px] font-black uppercase px-1.5 py-0.5 rounded mt-0.5 shrink-0`}>
                      {style.label}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-[#3a1064] text-sm leading-snug">{n.title}</div>
                      <div className="text-xs font-bold text-[#3a1064]/80 mt-1 leading-snug">{n.body}</div>

                      {link && (
                        <a
                          data-testid="notif-payment-link"
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 inline-flex items-center gap-1.5 bg-[#ff2bd6] hover:bg-[#e119bf] text-white font-black text-xs px-3 py-1.5 rounded-lg border-2 border-[#3a1064] shadow-[0_2px_0_#3a1064] break-all"
                        >
                          <ExternalLink className="h-3 w-3 shrink-0" /> Open payment link
                        </a>
                      )}

                      {meet && (
                        <div className="mt-2 bg-yellow-100 border-2 border-[#ffd900] rounded-lg px-2 py-1.5 text-[11px] font-bold text-[#3a1064]">
                          <span className="font-black uppercase text-[9px] tracking-wider text-[#3a1064]/70">Meet-up</span>
                          <div className="mt-0.5">{meet}</div>
                        </div>
                      )}

                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-[10px] font-bold text-[#3a1064]/50">{formatRelative(n.created_at)}</span>
                        {link && (
                          <button
                            onClick={() => copy(link)}
                            className="text-[10px] font-black text-[#3a1064] hover:text-[#ff2bd6] flex items-center gap-1"
                          >
                            <Copy className="h-3 w-3" /> Copy link
                          </button>
                        )}
                        {!n.read && (
                          <button
                            data-testid={`mark-read-${n.id}`}
                            onClick={() => markRead(n.id)}
                            className="text-[10px] font-black text-[#3a1064]/70 hover:text-[#ff2bd6] ml-auto"
                          >
                            Mark read
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
